<template>
  <LoadingCard v-if="loading" />

  <Alert v-else-if="globalMessage">{{ globalMessage }}</Alert>

  <template v-else-if="isSuccessInvoiceView">
    <SuccessInvoicePayment />
  </template>

  <Alert v-else-if="isBasketEmpty">{{ __('basket_is_empty') }}</Alert>

  <template v-else>
    <PageTree />

    <template v-if="isOverviewView">
      <Products :disabled="isModeChangeLoading" />

      <div class="text-right text-14 font-medium mt-14 lg:text-10 lg:pr-20">
        {{ currency }}{{ Helper.formatPrice(getTotalPriceWithVat) }}
      </div>

      <div class="mt-20">
        <Alert class="mb-10" v-if="getErrorMessage">{{
          getErrorMessage
        }}</Alert>

        <div class="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <BonusCode :disabled="isModeChangeLoading" />
          </div>
          <div class="text-right">
            <Button
              size="big"
              :loading="isModeChangeLoading"
              @click="changeMode('checkout')"
              >{{ __('btn_proceed_to_checkout') }}
            </Button>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="isCheckoutView">
      <UserForm />

      <div class="text-center text-24 font-medium my-28">
        {{ currency }}{{ Helper.formatPrice(getTotalPriceWithVat) }}
      </div>

      <Banks />

      <div class="text-center mt-20">
        <Button
          size="big"
          :loading="isModeChangeLoading"
          @click="triggerGoToPay"
        >
          {{ __('btn_pay') }}
        </Button>
      </div>
    </template>

    <SuccessInvoicePayment v-else-if="isSuccessInvoiceView" />
  </template>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import useProduct from '@/stores/Product';
import useBasket from '@/stores/Basket';
import LoadingCard from '@/components/LoadingCard';
import Alert from '@/components/Alert';
import Products from '@/components/Products';
import Banks from '@/components/Banks';
import UserForm from '@/components/UserForm';
import PageTree from '@/components/PageTree/List';
import {
  isOverviewView,
  isCheckoutView,
  setCheckoutView,
  isSuccessInvoiceView,
  setSuccessInvoiceView
} from '@/stores/View';
import useCheckout from '@/stores/Checkout';
import Button from '@/components/Button';
import SuccessInvoicePayment from '@/components/SuccessInvoicePayment';
import BonusCode from '@/components/BonusCode';
import fieldValidator from '@/libraries/FieldValidator';
import Helper from '@/libraries/Helpers';

export default {
  name: 'ShopBasket',
  components: {
    LoadingCard,
    Alert,
    Products,
    UserForm,
    Banks,
    Button,
    PageTree,
    SuccessInvoicePayment,
    BonusCode
  },
  setup() {
    const { getProducts } = useProduct();

    const {
      initBasket,
      isBasketEmpty,
      getGlobalMessage,
      getExpiresAt,
      setExpiresAt,
      getSettings,
      initCheckout,
      getTotalPriceWithVat,
      checkInventory,
      getErrorMessage
    } = useBasket();
    const { getFilteredUserFields, goToBank, saveClientData } = useCheckout();

    const loading = ref(true);

    onMounted(async () => {
      await initBasket();
      loading.value = false;
    });

    let lastExpiresAt = null;
    setInterval(() => {
      const expiresAt = getExpiresAt();
      if (expiresAt) {
        if (Date.now() >= expiresAt && lastExpiresAt !== expiresAt) {
          setTimeout(() => {
            initBasket();
          }, 2500);
          setExpiresAt(null);
          lastExpiresAt = expiresAt;
        }
      }
    }, 1000);

    watch(
      () => getExpiresAt(),
      time => {
        if (time) {
          window.parent.postMessage(
            JSON.stringify({
              key: 'shop_purchase_timer',
              expires_at: time
            }),
            '*'
          );
        } else {
          window.parent.postMessage(
            JSON.stringify({
              key: 'shop_purchase_timer',
              expires_at: null
            }),
            '*'
          );
        }
      },
      {
        immediate: true
      }
    );

    watch(
      () => getProducts(),
      products => {
        window.parent.postMessage(
          JSON.stringify({
            key: 'shop_basket_preview',
            products
          }),
          '*'
        );
      }
    );

    const isModeChangeLoading = ref(false);

    const changeMode = async type => {
      isModeChangeLoading.value = true;

      if (type === 'checkout') {
        const { success } = await checkInventory();
        if (success) {
          await initCheckout();
          setCheckoutView();
        }
      }

      isModeChangeLoading.value = false;
    };

    const triggerGoToPay = async () => {
      isModeChangeLoading.value = true;
      let fieldAnyErrors = false;
      getFilteredUserFields().forEach(field => {
        const validated = fieldValidator(field);
        if (!validated) {
          fieldAnyErrors = true;
        }
      });
      if (!fieldAnyErrors) {
        await saveClientData();
        const { success, redirecting } = await goToBank();
        if (success && !redirecting) {
          setSuccessInvoiceView();
        }
      }
      isModeChangeLoading.value = false;
    };

    return {
      loading,
      isBasketEmpty: computed(() => isBasketEmpty()),
      globalMessage: computed(() => getGlobalMessage()),
      currency: computed(() => getSettings('currency')),
      getTotalPriceWithVat: computed(() => getTotalPriceWithVat()),
      getErrorMessage: computed(() => getErrorMessage()),
      changeMode,
      triggerGoToPay,
      isModeChangeLoading,
      Helper,
      isOverviewView,
      isCheckoutView,
      isSuccessInvoiceView
    };
  }
};
</script>
